import React, { memo } from "react";
import {
    Box,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    Paper,
    Typography,
    useTheme,
} from "@mui/material";
import { InsertDriveFile, Error as ErrorIcon } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InvoiceMenu from "./InvoiceMenu";
import LineItemsBox from "./LineItemsBox";

const CAN_SUBMIT_STATUSES = ["error", "success"];

function InvoiceItemCard({
    invoice,
    index,
    updateInvoice,
    handleDeleteInvoice,
    projectDocket,
}) {
    const theme = useTheme();
    const isSuccessOrError = CAN_SUBMIT_STATUSES.includes(invoice.status);

    const handleUpdate = (field, value) => {
        updateInvoice(index, field, value);
    };

    return (
        <Paper
            elevation={0}
            id={`invoice-container-${index}`}
            sx={{
                mb: 6,
                border:
                    invoice.status === "error"
                        ? `2px solid ${theme.palette.error.dark}`
                        : `1px solid ${theme.palette.text.secondary}`,
                overflow: "hidden",
                borderRadius: 2,
                boxShadow: "none",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    bgcolor: "background.paper",
                    borderBottom: invoice.isCollapsed
                        ? "none"
                        : `1px solid ${theme.palette.text.secondary}`,
                    p: 1,
                    px: 2,
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InsertDriveFile
                        fontSize="small"
                        color={invoice.status === "error" ? "error" : "action"}
                    />
                    <Typography variant="subtitle2">
                        {invoice.file.name}
                    </Typography>
                    <InvoiceMenu onDelete={() => handleDeleteInvoice(index)} />
                </Box>
                <IconButton
                    onClick={() =>
                        handleUpdate("isCollapsed", !invoice.isCollapsed)
                    }
                    size="small"
                >
                    {invoice.isCollapsed ? (
                        <ExpandMoreIcon />
                    ) : (
                        <ExpandLessIcon />
                    )}
                </IconButton>
            </Box>

            <Collapse in={!invoice.isCollapsed}>
                <Grid container flexWrap="nowrap" sx={{ height: "80vh" }}>
                    {/* Left Panel: File Preview */}
                    <Grid
                        item
                        xs={6}
                        sx={{
                            height: "100%",
                            overflowY: "auto",
                            borderRight: `1px solid ${theme.palette.text.secondary}`,
                        }}
                    >
                        <Box
                            sx={{
                                minHeight: "100%",
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "center",
                                p: 2,
                            }}
                        >
                            {invoice.fileUrl &&
                                (invoice.isPdf ? (
                                    <iframe
                                        src={`${invoice.fileUrl}#toolbar=1&navpanes=0&scrollbar=0`}
                                        width="100%"
                                        height="800px"
                                        style={{
                                            minHeight: "100%",
                                            border: "none",
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={invoice.fileUrl}
                                        alt="Preview"
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                        }}
                                    />
                                ))}
                        </Box>
                    </Grid>

                    {/* Right Panel: Form / Status */}
                    <Grid
                        item
                        xs={6}
                        id={`invoice-form-${index}`}
                        sx={{
                            height: "100%",
                            overflowY: "auto",
                            p: 3,
                            bgcolor: "background.default",
                        }}
                    >
                        {invoice.status === "loading" ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                }}
                            >
                                <CircularProgress sx={{ mb: 2 }} />
                                <Typography color="text.secondary">
                                    Reading document with AI...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {invoice.status === "error" && (
                                    <Box
                                        sx={{
                                            mb: 3,
                                            p: 2,
                                            bgcolor: theme.palette.error.dark,
                                            color: "#fff",
                                            borderRadius: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <ErrorIcon sx={{ color: "#fff" }} />
                                        <Typography color="inherit">
                                            {invoice.error}
                                        </Typography>
                                    </Box>
                                )}
                                {isSuccessOrError && (
                                    <LineItemsBox
                                        invoice={invoice}
                                        onUpdate={handleUpdate}
                                        projectDocket={projectDocket}
                                    />
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Collapse>
        </Paper>
    );
}

// React.memo absolutely blocks render bubbling from the parent context, solving O(N) lag instantly!
export default memo(InvoiceItemCard);
